import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'

// ── G1 status ─────────────────────────────────────────────────────────────────

// "Expiring soon" = within 90 days of today
const EXPIRING_SOON_DAYS = 90

type G1Status = 'valid' | 'expiring_soon' | 'expired'

function getG1Status(expiryDateStr: string | null): G1Status | null {
  if (!expiryDateStr) return null
  const expiry = new Date(expiryDateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  if (expiry < today) return 'expired'
  const soonThreshold = new Date(today)
  soonThreshold.setDate(today.getDate() + EXPIRING_SOON_DAYS)
  if (expiry <= soonThreshold) return 'expiring_soon'
  return 'valid'
}

// A translucent white pill — same device as LessonCard/Badge's WhiteBadge, so a status
// chip still reads clearly against the card's own teal background rather than the
// teal-on-teal (or red-on-teal) combinations a page-background-oriented palette gives.
const G1_STATUS_TEXT: Record<G1Status, string> = {
  valid:          '#fff',
  expiring_soon:  '#FFE1A8',
  expired:        '#FFC2AE',
}
const G1_STATUS_LABEL: Record<G1Status, string> = {
  valid: 'Valid', expiring_soon: 'Expiring soon', expired: 'Expired',
}

function G1StatusBadge({ status }: { status: G1Status }) {
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, flexShrink: 0,
      background: 'rgba(255,255,255,0.18)', color: G1_STATUS_TEXT[status],
      font: '600 11.5px Inter, sans-serif',
    }}>
      {G1_STATUS_LABEL[status]}
    </span>
  )
}

// ── Card shell ────────────────────────────────────────────────────────────────
// Teal-gradient, white-text treatment — matching the dashboard stat cards and the
// calendar's LessonCard, so the student portal's own landing page carries the same
// "something worth noticing" visual language instead of looking like a different app.

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: 'linear-gradient(155deg, #0F7C6C, #0B5D51)',
      borderRadius: 14,
      padding: '22px 22px',
      ...style,
    }}>
      {children}
    </div>
  )
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ font: 'var(--text-section)', color: '#fff', marginBottom: 14 }}>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
      <span style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(255,255,255,0.7)' }}>{label}</span>
      <span style={{ font: '500 13.5px Inter, sans-serif', color: '#fff', textAlign: 'right' }}>{value}</span>
    </div>
  )
}

// ── Instructor data ────────────────────────────────────────────────────────────

interface InstructorSummary {
  full_name: string | null
  service_area: string | null
}

interface InstructorQueryRow {
  instructor_id: string
  instructor: InstructorSummary | null
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function StudentHome() {
  const { profile, studentProfile, profileLoading } = useAuth()

  const [instructor, setInstructor] = useState<InstructorSummary | null | undefined>(undefined)
  const [instructorError, setInstructorError] = useState<string | null>(null)
  const [instructorLoading, setInstructorLoading] = useState(true)

  useEffect(() => {
    if (!profile?.id) return

    let cancelled = false

    async function fetchInstructor() {
      setInstructorLoading(true)
      setInstructorError(null)

      // Join instructor_student_relationships → profiles (instructor side)
      // and → instructor_profiles (for service_area).
      // RLS (migration 027) allows the student to read their instructor's profiles row.
      // RLS on instructor_profiles does NOT currently grant student access to that table,
      // so we join through profiles only and separately query instructor_profiles
      // via the relationship.
      // Disambiguate the two FKs to profiles (instructor_id / student_id)
      // by naming the column: profiles!instructor_id
      const { data, error } = await supabase
        .from('instructor_student_relationships')
        .select(`
          instructor_id,
          instructor:profiles!instructor_id (
            full_name
          )
        `)
        .eq('student_id', profile!.id)
        .eq('status', 'active')
        .maybeSingle()

      if (cancelled) return

      if (error) {
        console.error('[StudentHome] instructor fetch error:', error.message)
        setInstructorError('Unable to load instructor information.')
        setInstructor(null)
      } else if (!data) {
        // No active instructor relationship
        setInstructor(null)
      } else {
        const row = data as unknown as InstructorQueryRow
        // instructor_profiles.service_area is not accessible to students under current RLS.
        // We expose only what profiles provides (full_name).
        setInstructor({
          full_name: row.instructor?.full_name ?? null,
          service_area: null,
        })
      }

      setInstructorLoading(false)
    }

    fetchInstructor()
    return () => { cancelled = true }
  }, [profile?.id])

  // ── Loading ──────────────────────────────────────────────────────────────────

  // Only wait for instructorLoading when we actually have a profile to query against.
  if (profileLoading || (!!profile?.id && instructorLoading)) {
    return (
      <div>
        <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
          Your road-test readiness
        </div>
        <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 24 }}>
          Loading…
        </div>
      </div>
    )
  }

  // ── Derive G1 status ─────────────────────────────────────────────────────────

  const g1Status = getG1Status(studentProfile?.g1_expiry_date ?? null)

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
        Your road-test readiness
      </div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 28 }}>
        Welcome, {profile?.full_name ?? 'Student'}
      </div>

      <div className='g2'>
        {/* G1 Licence card */}
        <Card>
          <CardLabel>G1 Licence</CardLabel>

          {studentProfile ? (
            <div>
              <Row
                label="Licence number"
                value={studentProfile.g1_licence_number ?? <span style={{ color: 'rgba(255,255,255,0.5)' }}>Not on file</span>}
              />
              <Row
                label="Issue date"
                value={formatDate(studentProfile.g1_issue_date)}
              />
              <Row
                label="Expiry date"
                value={
                  studentProfile.g1_expiry_date
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        {formatDate(studentProfile.g1_expiry_date)}
                        {g1Status && <G1StatusBadge status={g1Status} />}
                      </span>
                    : <span style={{ color: 'rgba(255,255,255,0.5)' }}>—</span>
                }
              />
            </div>
          ) : (
            <div style={{ font: '400 13.5px/1.6 Inter, sans-serif', color: 'rgba(255,255,255,0.75)', paddingTop: 4 }}>
              G1 details have not been added yet. Your instructor can update these when you begin lessons.
            </div>
          )}
        </Card>

        {/* Instructor card */}
        <Card>
          <CardLabel>Your Instructor</CardLabel>

          {instructorError ? (
            <div style={{ font: '400 13.5px Inter, sans-serif', color: '#FFC2AE' }}>
              {instructorError}
            </div>
          ) : instructor ? (
            <div>
              <Row
                label="Name"
                value={instructor.full_name ?? <span style={{ color: 'rgba(255,255,255,0.5)' }}>—</span>}
              />
            </div>
          ) : (
            <div style={{ font: '400 13.5px/1.6 Inter, sans-serif', color: 'rgba(255,255,255,0.75)', paddingTop: 4 }}>
              No active instructor assigned. Accept an invitation from your instructor to get started.
            </div>
          )}
        </Card>
      </div>

      {/* Coming soon notice */}
      <div className='banner-info' style={{ marginTop: 24 }}>
        Course videos, skill checklist, and mock G2 test will be available once your instructor assigns them.
      </div>
    </div>
  )
}
